import { merge } from '../../../helper';

import type { CustomFile } from '../types';

import type { TypeZipOptions } from '../zip/types';

import expandZip from '../zip/expandFiles';

import clearInputEvent from './clearInputEvent';
import getOriginalEvent, { type TypeEventWithOriginalEvent } from './getOriginalEvent';
import handleInputEvent, { type TypeInputEventCancelReason } from './handleInputEvent';
import validateFiles, { type TypeFileValidateResult, type TypeValidateFiles } from './validateFiles';

// ----------------------------------------------------------------------------

export interface IEventListener extends TypeValidateFiles {
    merge?: IEventListener | string;
    fnSuccess?: (files: CustomFile[], result: TypeFileValidateResult) => void | Promise<void>;
    fnError?: (rejected: TypeFileValidateResult['rejected'], result: TypeFileValidateResult) => void | Promise<void>;
    fnConfirmFiles?: (totalFiles: number, evt: DragEvent) => boolean | Promise<boolean>;
    /*
     * Files a dropped folder has to hold before fnConfirmFiles is asked. Defaults
     * to 1, so every folder is confirmed; raise it to let small drops through.
     */
    confirmThreshold?: number;
    /*
     * Progress of the two phases of a folder drop — walking it, then reading it.
     * Both are optional; without them a large drop reports nothing until it ends.
     */
    fnScanProgress?: (found: number) => void;
    fnReadProgress?: (done: number, total: number) => void;
    /*
     * Opt in to unpacking the zips in a selection, so that what reaches fnSuccess is
     * the files inside them. Off by default: a caller that means to upload the
     * archive itself must keep being handed the archive.
     */
    zip?: boolean | TypeZipOptions;
    // Archives opened so far, out of how many the selection holds.
    fnZipProgress?: (done: number, total: number) => void;
    /*
     * The drop ended with no files: declined at the confirmation, nothing usable in
     * it, or it failed outright. Neither fnSuccess nor fnError fires in that case.
     */
    fnCancel?: (reason: TypeInputEventCancelReason) => void;
    /*
     * A selection that arrived while the previous one was still being handled, and
     * was dropped on the floor. It is not one of the terminal callbacks — nothing
     * was started, so nothing ended — and it deliberately does not go out through
     * fnCancel: that would have the caller write a failure over the progress it is
     * still reporting for the selection that is genuinely running.
     */
    fnBusy?: () => void;
    /*
     * Asked once per event for the signal that aborts that event's walk, read and
     * validation. A factory rather than a signal because an AbortSignal is spent
     * once: a caller hands back a fresh controller's signal per event, and would
     * otherwise be stuck with one that stays aborted from the first cancel on.
     */
    fnSignal?: () => AbortSignal | undefined;
}

export type TypeInputEventLike = TypeEventWithOriginalEvent;

export type TypeEventListenerReturn = (evt: TypeInputEventLike) => Promise<TypeFileValidateResult | undefined>;

// ----------------------------------------------------------------------------

function eventListener(config: IEventListener = {}): TypeEventListenerReturn {
    const defConfig: IEventListener = {
        fnConfirmFiles: (totalFiles): boolean => {
            if (typeof globalThis.confirm !== 'function') {
                return true;
            }

            return globalThis.confirm(`Do you want to upload ${totalFiles} files?`);
        },
    };

    merge(defConfig, config);

    // ------------------------------------------------------------------------

    /*
     * A selection that is still being handled. A second one landing on top of it
     * would interleave its progress with the first — two walks reporting through the
     * same fnScanProgress — and hand the caller two sets of files to upload.
     *
     * It stays raised across fnSuccess, which for a caller that scans or uploads in
     * it is the long part, so what is ignored is reported through fnBusy rather than
     * only warned about: minutes of a dropzone that answers nothing look broken.
     */
    let running = false;

    // ------------------------------------------------------------------------

    return async function (evt: TypeInputEventLike): Promise<TypeFileValidateResult | undefined> {
        const nativeEvent = getOriginalEvent(evt);

        nativeEvent.preventDefault();

        if (running) {
            console.warn('The previous selection is still being handled; ignoring this one.');
            defConfig.fnBusy?.();
            return undefined;
        }

        running = true;

        /*
         * Asked for once and passed down to all three phases. Asking again per phase
         * would mint a controller per phase in a caller that builds one here, and
         * every phase but the last would be cancelling something nothing holds.
         */
        const signal = defConfig.fnSignal?.();

        /*
         * Whatever the drop turns out to hold, one of fnSuccess / fnError / fnCancel
         * has to fire: a caller that started reporting progress on the walk has
         * nothing else telling it the walk is over. The cancel is the fallback of
         * the three, so it goes out at most once and only if neither of the others
         * has already run to completion for this event.
         */
        let reported = false;

        const fnCancel = function (reason: TypeInputEventCancelReason): void {
            if (reported) {
                return;
            }

            reported = true;
            defConfig.fnCancel?.(reason);
        };

        /*
         * Best effort, and once: the dataTransfer of a drop is already past its
         * read/write window by the time the first await returns, and a throw here
         * would reject a handler that did its work.
         */
        let cleared = false;

        const clearInput = function (): void {
            if (cleared) {
                return;
            }

            cleared = true;

            try {
                clearInputEvent(nativeEvent);
            } catch (err) {
                console.warn('Could not clear the input:', err);
            }
        };

        try {
            const data = await handleInputEvent(nativeEvent, {
                fnConfirmFiles: defConfig.fnConfirmFiles,
                confirmThreshold: defConfig.confirmThreshold,
                fnScanProgress: defConfig.fnScanProgress,
                fnReadProgress: defConfig.fnReadProgress,
                fnCancel: fnCancel,
                signal: signal,
            });

            /*
             * The files are held in memory from here on, so the input is freed for
             * the next pick right away rather than at the end — which, for a caller
             * that uploads inside fnSuccess, is the end of the upload. Until it is
             * cleared, picking the same file again fires no change event.
             */
            clearInput();

            /*
             * handleInputEvent reports each of its own empty returns; this is the
             * backstop for a path that ever forgets to.
             */
            if (data.length === 0) {
                fnCancel('no-files');
                return undefined;
            }

            /*
             * Before the validation, so that what is accepted or rejected is what the
             * archives actually hold rather than the archives themselves — a caller
             * accepting only '.dcm' would otherwise turn every zip away unopened.
             */
            const files = await expandZipFiles(data, defConfig, signal);

            /*
             * Every archive in the selection failed to open, or held nothing that could
             * be read. Reported as an empty drop, which is what it is.
             */
            if (files.length === 0) {
                fnCancel('no-files');
                return undefined;
            }

            const validatedFiles = await validateFiles(files, { ...defConfig, signal });

            /*
             * An abort part-way through leaves a result that covers only some of the
             * files, so it is the signal that is read here, not the result: handing
             * a caller a slice of its own drop as if it were the whole of it is the
             * one outcome worse than saying nothing.
             */
            if (signal?.aborted === true) {
                fnCancel('cancelled');
                return undefined;
            }

            /*
             * Rejections go out first: awaiting fnSuccess ahead of them holds the
             * error back for as long as the caller's upload takes.
             */
            if (validatedFiles.rejected.length > 0 && typeof defConfig.fnError === 'function') {
                await defConfig.fnError(validatedFiles.rejected, validatedFiles);
                reported = true;
            }

            /*
             * Marked as reported only once the callback is through: one that throws
             * has told the caller nothing, and the catch below is what has to reach
             * it — for a caller that uploads in fnSuccess, that is where a failure
             * is most likely to come from in the first place.
             */
            if (validatedFiles.accepted.length > 0) {
                if (typeof defConfig.fnSuccess === 'function') {
                    await defConfig.fnSuccess(validatedFiles.accepted, validatedFiles);
                }

                reported = true;
            }

            /*
             * Nothing accepted and nothing said about it: every file was skipped by
             * the custom validator, or rejected with no fnError wired to hear it.
             */
            if (!reported) {
                fnCancel('no-files');
            }

            return validatedFiles;
        } catch (err) {
            console.error('Error handling files:', err);

            /*
             * Same reason as the cancel paths: a drop that fails without a word
             * leaves the caller reporting a walk that is over. A fnSuccess or
             * fnError that threw lands here too, and is reported, since neither got
             * far enough to count as having spoken for this event.
             */
            fnCancel('error');

            return undefined;
        } finally {
            clearInput();
            // eslint-disable-next-line require-atomic-updates -- the flag is raised before the first await and only ever lowered here, on the one handler that raised it
            running = false;
        }
    };
}

// ----------------------------------------------------------------------------

/*
 * Nothing to do unless the caller asked for it, and nothing to do for a selection
 * with no archive in it either — expandFiles returns that one untouched.
 */
async function expandZipFiles(
    files: CustomFile[],
    config: IEventListener,
    signal: AbortSignal | undefined,
): Promise<CustomFile[]> {
    if (config.zip == null || config.zip === false) {
        return files;
    }

    return expandZip(files, {
        ...(config.zip === true ? {} : config.zip),
        signal: signal,
        fnProgress: config.fnZipProgress,
    });
}

// ----------------------------------------------------------------------------

export default eventListener;
